/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useRef} from 'react';
import {getLayerRenderer, RendererMapType} from '../helpers/MapViewUtils';

/**
 * Manages and resolves a layer renderer based on zoom of the view.
 *
 * The hook stores three renderer variants (Normal, Medium, Large) using refs
 * and returns helper functions to:
 *  - Set the renderer map once it is created
 *  - Retrieve the appropriate renderer based on the current zoom level
 *
 * Refs are used instead of state to avoid unnecessary re-renders while
 * keeping renderer instances stable across renders.
 *
 * @returns An API for setting and resolving a zoom-based layer renderer
 * 
 * ```typescript
 * import useLayerRendererWithIconSize from '../hooks/useLayerRendererWithIconSize';
 * 
 *  const {
 *    setLayerRendererWithIconSize,
 *    getLayerRendererWithIconSize
 *  } = useLayerRendererWithIconSize();
 * ```
 */
export const useLayerRendererWithIconSize = () => {
  // Refs to store renderer instances for each icon size
  const normalIconSizeRendererRef = useRef<__esri.Renderer | null>(null);
  const mediumIconSizeRendererRef = useRef<__esri.Renderer | null>(null);
  const largeIconSizeRendererRef  = useRef<__esri.Renderer | null>(null);

  /**
   * Cleanup renderer references on unmount to prevent stale references
   * and allow proper garbage collection.
   */
  useEffect(() => {
    return () => {
      normalIconSizeRendererRef.current = null;
      mediumIconSizeRendererRef.current = null;
      largeIconSizeRendererRef.current  = null;
    };
  }, []);

  /**
   * Stores the renderer map by icon size.
   * This is typically called once when renderers are created or updated.
   */
  const setLayerRendererWithIconSize = useCallback((rendererMap: RendererMapType) => {
    normalIconSizeRendererRef.current = rendererMap?.Normal;
    mediumIconSizeRendererRef.current = rendererMap?.Medium;
    largeIconSizeRendererRef.current  = rendererMap?.Large;
  }, []);

  /**
   * Resolves and returns the appropriate renderer based on the given zoom level.
   *
   * @param zoom - Current map zoom level
   * @returns The resolved renderer or null if renderers are not initialized
   */
  const getLayerRendererWithIconSize = useCallback(zoom => {
    const normalIconSizeRenderer = normalIconSizeRendererRef.current;
    const mediumIconSizeRenderer = mediumIconSizeRendererRef.current;
    const largeIconSizeRenderer  = largeIconSizeRendererRef.current;
  
    // Ensure all renderer variants are available before resolving
    if (normalIconSizeRenderer && mediumIconSizeRenderer && largeIconSizeRenderer) {
      const layerRenderer: RendererMapType = {
        Normal : normalIconSizeRenderer,
        Medium : mediumIconSizeRenderer,
        Large  : largeIconSizeRenderer
      };
  
      return getLayerRenderer(zoom, layerRenderer);
    }
    return null;
  }, [getLayerRenderer]);

  return {
    setLayerRendererWithIconSize,
    getLayerRendererWithIconSize,
    normalIconSizeRendererRef,
    mediumIconSizeRendererRef,
    largeIconSizeRendererRef
  };
};

export default useLayerRendererWithIconSize;
